import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { KineticLine } from "./KineticLine";
import { BookCover } from "./BookCover";
import { BOOKS } from "../data/books";

const FLOAT = [
  { rotate: -10, y: [0, -14, 0], duration: 7, parallax: -160, cls: "right-[24%] top-[8%] w-[26%] z-10" },
  { rotate: 5, y: [0, -10, 0], duration: 8.5, parallax: -70, cls: "right-[2%] top-[26%] w-[30%] z-20" },
  { rotate: 13, y: [0, -18, 0], duration: 6, parallax: -240, cls: "right-[18%] bottom-[2%] w-[22%] z-0" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const py1 = useTransform(scrollYProgress, [0, 1], [0, FLOAT[0].parallax]);
  const py2 = useTransform(scrollYProgress, [0, 1], [0, FLOAT[1].parallax]);
  const py3 = useTransform(scrollYProgress, [0, 1], [0, FLOAT[2].parallax]);
  const parallax = [py1, py2, py3];

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 70% 55% at 68% 30%, rgba(230,57,70,0.09), transparent 65%)" }}
      />

      {/* Floating books — the 3D parallax moment */}
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[46%] md:block lg:pointer-events-auto">
        {BOOKS.map((book, i) => (
          <motion.div
            key={book.id}
            className={`absolute ${FLOAT[i].cls}`}
            style={{ y: parallax[i] }}
            initial={{ opacity: 0, y: 120, rotate: FLOAT[i].rotate }}
            animate={{ opacity: 1, y: 0, rotate: FLOAT[i].rotate }}
            transition={{ duration: 1.4, delay: 0.9 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.div
              animate={{ y: FLOAT[i].y }}
              transition={{ duration: FLOAT[i].duration, repeat: Infinity, ease: "easeInOut", delay: i * 0.7 }}
            >
              <BookCover book={book} interactive={false} />
            </motion.div>
          </motion.div>
        ))}
      </div>

      {/* Kinetic copy */}
      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pt-24 md:px-12">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mb-10 flex items-center gap-4"
        >
          <span className="h-px w-12 bg-[#E63946]" />
          <p className="font-sans text-[10px] font-bold uppercase tracking-[0.4em] text-[#A8A29E] md:text-xs">
            Samkhya Publishing presents
          </p>
        </motion.div>

        <h1 className="font-serif text-[clamp(3.4rem,10.5vw,10rem)] font-black leading-[0.88] tracking-tighter">
          <KineticLine delay={0.45}>STORIES</KineticLine>
          <KineticLine delay={0.6} className="italic font-medium text-[#A8A29E]">
            that wake
          </KineticLine>
          <KineticLine delay={0.75}>
            THE MIND<span className="text-[#E63946]">.</span>
          </KineticLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-10 max-w-md font-sans text-base font-light leading-relaxed text-[#A8A29E] md:text-lg"
        >
          Three books by Nicholas K F Matte — adventure, meditation and action —
          from a CRA-registered independent Canadian publisher.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          className="mt-10 flex flex-wrap items-center gap-5"
        >
          <a
            href="#chapter-01"
            data-testid="hero-explore-button"
            className="group flex items-center gap-3 bg-[#E63946] px-7 py-4 font-sans text-xs font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-[#F4F4F0] hover:text-[#0C0A09]"
          >
            Explore the books
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
          </a>
          <a
            href="#ad-studio"
            data-testid="hero-adstudio-button"
            className="group flex items-center gap-3 border border-white/20 px-7 py-4 font-sans text-xs font-bold uppercase tracking-[0.25em] text-[#F4F4F0] transition-colors duration-300 hover:border-[#E63946] hover:text-[#E63946]"
          >
            Generate an ad
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.7 }}
          className="mt-16 flex gap-10 border-t border-white/10 pt-6 md:mt-24"
        >
          {[
            ["03", "Books"],
            ["01", "Publisher"],
            ["CA", "CRA Registered"],
          ].map(([num, label]) => (
            <div key={label}>
              <p className="font-serif text-2xl font-bold md:text-3xl">{num}</p>
              <p className="mt-1 font-sans text-[10px] uppercase tracking-[0.25em] text-[#A8A29E]">{label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
